//noinspection JSFileReferences
import * as R from 'ramda'
import axios from 'axios'
import delay from 'lodash/delay'
import router from '@/router'
import { default_route, normalize_role } from '@/utils/role'
import { resolve_error_action } from '@/utils/http-error'
import { is_token_expired } from '@/utils/auth-token'
import { sessionBus } from '@/utils/session'
import Channel from 'tab-channel'

//noinspection JSUnusedGlobalSymbols
/** @namespace this.$ls */
export default {

    install(Vue) {

        // Broadcast-Channel erstellen
        const logoutChannel = new Channel('logout')

        // Nach Logout Fenster refresehen
        logoutChannel.onmessage = () => location.reload()

        const auth = new Vue({

            created() {
                // Auth-Token in Axios setzen
                axios.defaults.headers.common['Authorization'] = this.token

                // Auth-Refresh queuen
                this.queueRefresh()
            },

            computed: {

                activeRole() {
                    return this.$ls.get('activeRole')
                },

                activeRoleId() {
                    return this.$ls.get('activeRoleId')
                },

                canBeZmuekish() {
                    return this.$ls.get('canBeZmuekish')
                },

                companyId() {
                    return this.$ls.get('companyId')
                },

                isLoggedIn() {
                    return !R.isNil(this.token)
                },

                isZmuekish() {
                    return this.$ls.get('isZmuekish')
                },

                roleIds() {
                    return this.$ls.get('roleIds')
                },

                personId() {
                    return this.$ls.get('personId')
                },

                token() {
                    return this.$ls.get('token')
                },

                userId() {
                    return this.$ls.get('userId')
                },

                username() {
                    return this.$ls.get('username')
                }

            },

            methods: {

                async changeRole(role) {
                    // Rolle wechseln
                    const response = await axios.patch('/api/v1/auth', { role })

                    // Benutzerdaten Plugin akualisieren
                    this.store(response)

                    return response.data
                },

                async login(username, password) {
                    const credentials = { username, password }

                    // Login durchfuehren
                    const response = await axios.post('/api/v1/auth/login', credentials)

                    // User speichern
                    this.store(response)
                },

                async logout() {
                    this.$ls.clear()

                    // Alle Tabs über Logout informieren
                    await delay(() => logoutChannel.postMessage(undefined), 250)
                },

                queueRefresh() {
                    // Alle 5 Minuten refreshen
                    delay(this.refresh, 1000 * 60 * 5)
                },

                store({ data, headers }) {
                    // Daten in Storage setzen
                    this.$ls.set('activeRole', normalize_role(data.activeRoleId))
                    this.$ls.set('activeRoleId', data.activeRoleId)
                    this.$ls.set('canBeZmuekish', data.canBeZmuekish)
                    this.$ls.set('companyId', data.companyId)
                    this.$ls.set('isZmuekish', data.isZmuekish)
                    this.$ls.set('roleIds', data.roleIds)
                    this.$ls.set('personId', data.personId)
                    this.$ls.set('token', headers.authorization)
                    this.$ls.set('userId', data.userId)
                    this.$ls.set('username', data.username)

                    axios.defaults.headers.common['Authorization'] = this.$ls.get('token')
                },

                refresh() {
                    // Nur refresehen, wenn Benutzer eingeloggt
                    if (!this.isLoggedIn) {
                        // Authrefresh queuen
                        return this.queueRefresh()
                    }

                    return axios.patch('/api/v1/auth', {})
                        .then(this.store)
                        .then(this.queueRefresh)
                        .catch(() => {
                            // Token abgelaufen oder ungültig – Session leeren und zur Loginseite
                            this.$ls.clear()
                            if (router.currentRoute.name !== 'app/login') {
                                router.push({ name: 'app/login' })
                            }
                        })
                }

            },

        })

        router.beforeEach((to, from, next) => {
            if (to.name !== 'app/login' && !localStorage.getItem('zmk_token')) {
                next({ name: 'app/login' })
                return
            }

            // Restrict routes that require ZMÜK membership to zmuekish users only
            if (to.matched.some(record => record.meta && record.meta.requiresZmuekish) && !auth.isZmuekish) {
                next(default_route(auth.activeRoleId))
                return
            }

            next()
        })

        // Globaler Axios-Interceptor: bei 401 Session leeren und zur Loginseite weiterleiten
        axios.interceptors.response.use(
            response => response,
            error => {
                const status = error.response && error.response.status

                const action = resolve_error_action(status, {
                    isLoggedIn: !!localStorage.getItem('zmk_token'),
                    isOnLoginPage: router.currentRoute.name === 'app/login',
                    isTokenExpired: is_token_expired()
                })

                if (action.type === 'login') {
                    // Nur weiterleiten, wenn wir nicht bereits auf der Loginseite sind
                    if (router.currentRoute.name !== 'app/login') {
                        auth.$ls.clear()
                        router.push({ name: 'app/login' })
                        // Kein reject – verhindert Fehlermeldung im aufrufenden Code nach dem Redirect
                        return new Promise(() => {})
                    }
                }

                if (action.type === 'session-expired') {
                    sessionBus.$emit('session-expired', router.currentRoute.fullPath)
                    // Kein reject – das Modal übernimmt die weitere Navigation
                    return new Promise(() => {})
                }

                if (action.type === 'error') {
                    if (router.currentRoute.name !== 'error') {
                        router.push({ name: 'error', params: { statusCode: action.statusCode } })
                    }
                    // Kein reject – verhindert Fehlermeldung im aufrufenden Code nach dem Redirect
                    return new Promise(() => {})
                }

                // Fehler durchreichen, damit z. B. die Login-Seite Feedback anzeigen kann
                return Promise.reject(error)
            }
        )

        Vue.$auth = auth
        Vue.prototype.$auth = auth

    }

}
